import { action, computed, makeObservable } from 'mobx';
import {toInt} from '../../../../../lib/helpers';
import {X9ContentStateStore} from '../../common/X9ContentStateStore';
import {MQDataPairState, MQMatchingQuestion, MQMatchingQuestionState} from '../models';
import {RightSideOption} from './RightSideSelect';

export class DropdownMatchingQuestionStore {
  constructor(readonly contentSnapshotId: number,
              readonly question: MQMatchingQuestion,
              readonly stateStore: X9ContentStateStore) {
    makeObservable(this);
  }

  get options() {
    return this.question.options;
  }

  get dataPairs() {
    return this.question.dataPairs;
  }

  @computed
  get state(): MQMatchingQuestionState | null {
    return this.stateStore.getContentState<MQMatchingQuestionState>(this.contentSnapshotId);
  }

  @computed
  get dataPairStates(): readonly MQDataPairState[] {
    const state = this.state;
    const dataPairState = this.question.dataPairs.map((value, key) => { return {matchedTo: null, checked: false}})
    return (state === null) ? dataPairState : state.DataPairStates;
  }

  @computed
  get rightSideOrder(): readonly number[] {
    const state = this.state;
    return state === null ? [] : state.RightSideOrder;
  }

  @computed
  get rightSideOptions(): readonly RightSideOption[] {
    const dataPairs = this.question.dataPairs;

    if (this.rightSideOrder.length > 0) {
      return this.rightSideOrder.map((index): RightSideOption => ({
        index: index.toString(10),
        label: dataPairs[index].rightContent
      }));
    } else {
      return this.question.dataPairs.map((value, key): RightSideOption => ({ 
        index: `${key}`,
        label: value.rightContent
      })).sort((a, b) => 0.5 - Math.random());
    }
  }

  @action
  check = () => {
    for (let index = 0; index < this.dataPairStates.length; index++) {
      const state = this.dataPairStates[index];
      if (state.checked || state.matchedTo === null)
        continue;
      this.updateDataPairState(index, {matchedTo: state.matchedTo, checked: true});
    }
  };

  @action
  select(index: number, value: string | null) {
    const matchedTo = value === null ? null : toInt(value);
    const state: MQDataPairState = {checked: false, matchedTo: matchedTo};
    this.updateDataPairState(index, state);
  }

  @action
  updateDataPairState(index: number, dataPairState: MQDataPairState) {
    const newDataPairStates = this.dataPairStates!.slice();
    newDataPairStates[index] = dataPairState;
    this.stateStore.setContentState<MQMatchingQuestionState>(
      this.contentSnapshotId,
      {RightSideOrder: this.rightSideOrder!, DataPairStates: newDataPairStates});
  }
}
